import { Command } from 'commander'
import { createApiClient, SCOPES } from '../http.js'
import { loadConfig, saveConfig } from '../config.js'
import { decodeJwtPayload } from '../jwt.js'
import { pollForIdentityToken, startDeviceAuthorization } from '../auth/deviceFlow.js'

export function registerLogin(program: Command): void {
  program
    .command('login')
    .description('Sign in to Verentis (browser device flow by default; --api-key/--token for non-interactive use)')
    .option('--api-url <url>', 'Verentis API gateway URL')
    .option('--api-key <key>', 'Verentis API key (vrt_...)')
    .option('--token <token>', 'Verentis identity token (exchanged per request for scoped access tokens)')
    .option('--sealing-key <key>', "the platform's base64 X25519 sealing PUBLIC key (used by `pack --encrypt`)")
    .action(async (options: { apiUrl?: string; apiKey?: string; token?: string; sealingKey?: string }) => {
      const config = await loadConfig()
      if (options.apiUrl) config.apiUrl = options.apiUrl
      if (options.sealingKey) config.sealingPublicKey = options.sealingKey
      if (!config.apiUrl) throw new Error('An API URL is required. Pass --api-url <url>.')

      if (options.apiKey) {
        if (!options.apiKey.startsWith('vrt_')) throw new Error('API keys start with "vrt_".')
        config.apiKey = options.apiKey
        delete config.identityToken
        delete config.refreshToken
        await saveConfig(config)
        console.log(`Configuration saved (API key @ ${config.apiUrl}).`)
        return
      }
      if (options.token) {
        config.identityToken = options.token
        delete config.apiKey
        delete config.refreshToken
        await saveConfig(config)
        console.log(`Configuration saved (identity token @ ${config.apiUrl}).`)
        return
      }

      // Device-code flow (RFC 8628): works over SSH — approve in any browser.
      const apiUrl = config.apiUrl.replace(/\/+$/, '')
      const authorization = await startDeviceAuthorization(apiUrl)

      console.log('To sign in, open this URL in a browser:')
      console.log(`\n  ${authorization.verification_uri_complete ?? authorization.verification_uri}\n`)
      console.log(`and confirm the code: ${authorization.user_code}`)
      console.log('\nWaiting for approval…')

      const identity = await pollForIdentityToken(apiUrl, authorization, () => process.stderr.write('.'))
      process.stderr.write('\n')

      config.identityToken = identity.idToken
      if (identity.refreshToken) config.refreshToken = identity.refreshToken
      else delete config.refreshToken
      delete config.apiKey
      await saveConfig(config)

      const claims = decodeJwtPayload(identity.idToken)
      console.log(`Signed in as ${claims?.name ?? claims?.email ?? claims?.sub ?? 'unknown'} @ ${config.apiUrl}.`)
      if (!claims?.account_id) {
        console.log('Note: no unambiguous account context — admin commands may need --account <id>.')
      }
    })

  program
    .command('logout')
    .description('Remove stored credentials')
    .action(async () => {
      const config = await loadConfig()
      delete config.apiKey
      delete config.identityToken
      delete config.refreshToken
      await saveConfig(config)
      console.log('Credentials removed.')
    })

  program
    .command('whoami')
    .description('Show the current identity, account and publisher profile')
    .action(async () => {
      const config = await loadConfig()
      const identityToken = process.env.VERENTIS_TOKEN ?? config.identityToken
      if (identityToken) {
        const claims = decodeJwtPayload(identityToken)
        if (claims) {
          const expiry = claims.exp ? new Date(claims.exp * 1000) : undefined
          const expired = expiry && expiry.getTime() <= Date.now()
          const renewable = Boolean(config.refreshToken) && !process.env.VERENTIS_TOKEN
          console.log(`User: ${claims.name ?? claims.email ?? claims.sub ?? '(unknown)'}${claims.sub ? ` (${claims.sub})` : ''}`)
          if (claims.account_id) console.log(`Account: ${claims.account_id}`)
          if (expiry) {
            const state = expired ? (renewable ? 'expired (auto-renews)' : 'EXPIRED') : 'expires'
            console.log(`Token ${state}: ${expiry.toISOString()}`)
          }
          if (renewable) console.log('Session: refresh token stored (stays signed in across expiries)')
        }
      } else if (process.env.VERENTIS_API_KEY ?? config.apiKey) {
        console.log('Credential: API key (identity claims not available)')
      }
      if (config.defaultWorkspaceId) console.log(`Workspace: ${config.defaultWorkspaceId} (default)`)

      const api = await createApiClient()
      const publisher = await api.request<{ name?: string; displayName?: string; isVerified?: boolean; signingKeys?: { kid: string; status: string }[] } | null>(
        'GET',
        '/v1/publishers/me',
        { scopes: [SCOPES.publisherRead] },
      ).catch(() => null)
      if (!publisher) {
        console.log('Publisher: none — create one with `verentis publisher create --name <name> --display-name "<display name>"`')
        return
      }
      console.log(`Publisher: ${publisher.name} (${publisher.displayName})${publisher.isVerified ? ' ✓ verified' : ''}`)
      for (const key of publisher.signingKeys ?? []) {
        console.log(`  key ${key.kid} [${key.status}]`)
      }
    })
}
